'use client';
/**
 * D'OÙ VIENNENT NOS INTERVALLES.
 *
 * ── Le problème qu'il règle ────────────────────────────────────────────────
 *
 * « Sur quoi repose cette valeur usuelle ? » est la première question d'un
 * auditeur. Aujourd'hui la réponse tient en trois mots : « d'une reprise ».
 * Deux cent trente-neuf intervalles vivants citent « Fiche du manuel » ou
 * « valeurs de référence de la fiche » — ce qui dit d'où la DONNÉE a été
 * copiée, jamais ce qui la FONDE.
 *
 * Ce qui la fonde, l'ISO 15189 §7.3.4 le nomme : la population servie, la
 * TECHNIQUE et l'AUTOMATE. Un intervalle de créatinine n'a pas la même valeur
 * selon qu'il sort d'une méthode de Jaffé ou enzymatique.
 *
 * Cet écran met les deux côte à côte — la source telle qu'elle est écrite, et
 * les techniques et appareils qui produisent réellement le paramètre — pour que
 * la source puisse être RÉÉCRITE en connaissance de cause, au panneau du
 * paramètre.
 *
 * ⚠️ IL NE RÉÉCRIT RIEN, et surtout pas d'un bloc. Substituer « Indiko 1,
 * méthode enzymatique » à « reprise du 18/08 » affirmerait que l'intervalle a
 * été VÉRIFIÉ sur cet automate — ce que personne n'a fait. Le rapprochement dit
 * sur quel appareil le paramètre est rendu : c'est le point de départ de la
 * vérification, pas son résultat.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { card, EmptyState, Skeleton } from '@/components/ui';
import { useGed } from '@/lib/store';
import { moteur } from './Registres';

import { Bouton } from '@/shared/Bouton';
type Classe = 'absente' | 'reprise' | 'proposition' | 'reference' | 'operation' | 'autre';

interface Parametre {
  code: string;
  nom: string;
  nbIntervalles: number;
  nbAdoptes: number;
  sources: { texte: string; classe: Classe; nb: number }[];
  techniques: { code: string; nom: string; principe?: string | null }[];
  equipements: { code: string; nom: string; parTechnique: string }[];
  manques: string[];
}

interface Etat {
  parametres: number;
  intervalles: number;
  parClasse: Record<Classe, number>;
  avecTechnique: number;
  avecEquipement: number;
  documentes: number;
}

/* Le vocabulaire est celui de la QUESTION posée, pas celui du champ. « reprise »
   ne dit rien à qui ouvre l'écran ; « d'où la donnée a été copiée » dit ce qui
   ne va pas. */
const CLASSES: Record<Classe, { court: string; long: string; couleur: string; fond: string }> = {
  absente: { court: 'aucune source', long: 'Aucune source écrite.', couleur: 'var(--red-600)', fond: 'var(--red-50)' },
  reprise: { court: 'une reprise', long: 'Dit d’où la donnée a été copiée, pas ce qui la fonde.', couleur: 'var(--amber-600)', fond: 'var(--amber-50)' },
  proposition: { court: 'proposition', long: 'Posée par la plateforme, jamais validée par le laboratoire.', couleur: 'var(--amber-600)', fond: 'var(--amber-50)' },
  reference: { court: 'référence', long: 'Une société savante, une recommandation, une notice.', couleur: 'var(--green-700)', fond: 'var(--green-50)' },
  operation: { court: 'opération', long: 'Une trace d’opération sur le registre — pas une source.', couleur: 'var(--text-muted)', fond: 'var(--surface-sunken)' },
  autre: { court: 'autre', long: 'Non classée.', couleur: 'var(--text-muted)', fond: 'var(--surface-sunken)' },
};

const eyebrow: React.CSSProperties = {
  fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '0.04em', color: 'var(--text-muted)',
};

export function OngletSources() {
  const ouvrirPanneau = useGed((s) => s.ouvrirPanneau);
  const [etat, setEtat] = useState<Etat | null>(null);
  const [params, setParams] = useState<Parametre[] | null>(null);
  const [erreur, setErreur] = useState('');
  const [filtre, setFiltre] = useState('');
  const [seulementIncomplets, setSeulementIncomplets] = useState(true);
  const [ouvert, setOuvert] = useState<string | null>(null);

  const charger = useCallback(async () => {
    const r = await moteur('manuel-ref/sources');
    if (!r.ok) { setErreur(r.message || 'Le relevé des sources n’a pas pu être lu.'); return; }
    setErreur('');
    setEtat(r.data.etat as Etat);
    setParams((r.data.parametres || []) as Parametre[]);
  }, []);

  /* ⚠️ FAUX POSITIF ASSUMÉ de `react-hooks/set-state-in-effect` (02/09/2026).
     `charger` est un `useCallback` ASYNC : son `setState` se produit APRÈS un
     `await`, donc dans une continuation, jamais dans le corps de l'effet. La
     règle ne franchit pas la frontière asynchrone et l'annonce quand même.
     C'est l'usage canonique d'un effet — « s'abonner à un système externe ».
     ⚠️ Le silence est CIBLÉ, ligne à ligne : désactiver la règle pour le
     fichier masquerait un vrai cas ajouté demain. */
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { void charger(); }, [charger]);

  if (erreur) return <EmptyState titre="Relevé indisponible" indice={erreur} />;
  if (!params || !etat) return <Skeleton rows={6} />;

  const q = filtre.trim().toLowerCase();
  const liste = params
    .filter((p) => (!seulementIncomplets || p.manques.length))
    .filter((p) => !q || p.code.toLowerCase().includes(q) || p.nom.toLowerCase().includes(q));

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.6, maxWidth: '92ch' }}>
        « Sur quoi repose cette valeur usuelle ? » est la première question d’un auditeur. Cet écran met
        la <strong>source écrite</strong> en regard de la <strong>technique</strong> et de
        l’<strong>automate</strong> qui produisent réellement le paramètre — ce que l’ISO 15189 §7.3.4
        désigne comme le fondement d’un intervalle. Le rapprochement passe par la relation
        paramètre → examen → technique → équipement, <strong>jamais par le nom</strong>.
      </p>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.6, maxWidth: '92ch' }}>
        Il <strong>ne réécrit rien</strong> : savoir sur quel appareil un paramètre est rendu est le
        point de départ de la vérification, pas son résultat. La source se corrige au panneau du
        paramètre, intervalle par intervalle.
      </p>

      {/* Le chiffrage en tête, et `documentes` en premier : c'est la seule
          mesure d'avancement qui ne se trompe pas — les autres comptent des
          symptômes. */}
      <div style={{ ...card, padding: 14, display: 'flex', gap: 22, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <div>
          <div style={eyebrow}>Documentés</div>
          <div style={{ fontSize: 20, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            {etat.documentes}
            <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--text-muted)' }}> / {etat.parametres}</span>
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>plus rien ne manque</div>
        </div>
        <div>
          <div style={eyebrow}>Intervalles vivants</div>
          <div style={{ fontSize: 20, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{etat.intervalles}</div>
        </div>
        <div>
          <div style={eyebrow}>Technique reliée</div>
          <div style={{ fontSize: 20, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{etat.avecTechnique}</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{etat.avecEquipement} jusqu’à l’automate</div>
        </div>
        {(['absente', 'reprise', 'proposition', 'reference'] as Classe[]).map((c) => (
          etat.parClasse[c] ? (
            <div key={c}>
              <div style={eyebrow}>{CLASSES[c].court}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: CLASSES[c].couleur, fontVariantNumeric: 'tabular-nums' }}>
                {etat.parClasse[c]}
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>paramètre(s)</div>
            </div>
          ) : null
        ))}
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <input value={filtre} onChange={(e) => setFiltre(e.target.value)}
          placeholder="Filtrer — code ou nom du paramètre"
          style={{
            flex: '1 1 260px', maxWidth: 380, fontSize: 12.5, padding: '6px 10px',
            border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)',
            background: 'var(--surface-raised)', color: 'var(--text-body)',
          }} />
        <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 12.5 }}>
          <input type="checkbox" checked={seulementIncomplets}
            onChange={(e) => setSeulementIncomplets(e.target.checked)} />
          Ne montrer que ce qui reste à documenter
        </label>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{liste.length} affiché(s)</span>
      </div>

      {!liste.length ? (
        <EmptyState titre="Rien à afficher"
          indice="Aucun paramètre ne correspond au filtre. Décochez « ce qui reste à documenter » pour voir aussi ceux dont la source est déjà en règle." />
      ) : liste.map((p) => {
        const deplie = ouvert === p.code;
        return (
          <div key={p.code} style={{ ...card, padding: 12 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <button type="button" className="ged-btn"
                title={`Ouvrir le paramètre ${p.code} sans quitter ce relevé`}
                onClick={() => ouvrirPanneau({ type: 'parametre', valeur: p.code, origine: 'Sources des intervalles' })}
                style={{
                  border: 'none', background: 'none', cursor: 'pointer', padding: 0,
                  fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700,
                  color: 'var(--color-primary-active)',
                }}>{p.code}</button>
              <strong style={{ fontSize: 13.5 }}>{p.nom}</strong>
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                {p.nbIntervalles} intervalle(s), {p.nbAdoptes} adopté(s)
              </span>
              {/* Les classes présentes, en pastilles : c'est ce qui se lit d'un
                  coup d'œil sur deux cents lignes. */}
              {[...new Set(p.sources.map((s) => s.classe))].map((c) => (
                <span key={c} title={CLASSES[c].long} style={{
                  fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)',
                  padding: '2px 9px', background: CLASSES[c].fond, color: CLASSES[c].couleur,
                }}>{CLASSES[c].court}</span>
              ))}
              <span style={{ flex: 1 }} />
              <span style={{ fontSize: 12.5, color: p.techniques.length ? 'var(--text-muted)' : 'var(--amber-600)' }}>
                {p.techniques.length
                  ? `${p.techniques.length} technique(s) · ${p.equipements.length} automate(s)`
                  : 'aucune technique reliée'}
              </span>
              <Bouton taille="sm" onClick={() => setOuvert(deplie ? null : p.code)}>
                {deplie ? 'Replier' : 'Détail'}
              </Bouton>
            </div>

            {deplie && (
              <div style={{ marginTop: 10, display: 'grid', gap: 10 }}>
                <div style={{ background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: '9px 11px' }}>
                  <div style={{ ...eyebrow, marginBottom: 5 }}>Ce que la source dit aujourd’hui</div>
                  {p.sources.map((s) => (
                    <div key={s.texte || 'vide'} style={{ fontSize: 12.5, lineHeight: 1.6 }}>
                      <span style={{
                        fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)',
                        padding: '1px 8px', marginInlineEnd: 6,
                        background: CLASSES[s.classe].fond, color: CLASSES[s.classe].couleur,
                      }}>{CLASSES[s.classe].court}</span>
                      {/* Le TEXTE BRUT est toujours montré à côté de son
                          étiquette : le classement est une heuristique sur le
                          libellé, et une heuristique qui masque sa matière
                          première ne se contredit pas. */}
                      {s.texte || <em style={{ color: 'var(--red-600)' }}>vide</em>}
                      <span style={{ color: 'var(--text-muted)' }}> — {s.nb} intervalle(s)</span>
                    </div>
                  ))}
                </div>

                <div style={{ background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: '9px 11px' }}>
                  <div style={{ ...eyebrow, marginBottom: 5 }}>Ce qui le produit — par la relation, pas par le nom</div>
                  {p.techniques.length ? p.techniques.map((t) => {
                    const eq = p.equipements.filter((e) => e.parTechnique === t.code);
                    return (
                      <div key={t.code} style={{ fontSize: 12.5, lineHeight: 1.6 }}>
                        <strong>{t.nom}</strong>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-muted)' }}> {t.code}</span>
                        {eq.length
                          ? <> — sur {eq.map((e) => `${e.nom} (${e.code})`).join(', ')}</>
                          : <span style={{ color: 'var(--amber-600)' }}> — aucun automate rattaché à cette technique</span>}
                        {t.principe && (
                          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>{t.principe}</div>
                        )}
                      </div>
                    );
                  }) : (
                    <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                      Aucune technique n’est reliée à ce paramètre par ses examens : rien ne dit par quelle
                      méthode il est rendu.
                    </div>
                  )}
                </div>

                {!!p.manques.length && (
                  <div style={{ padding: '9px 11px', borderRadius: 'var(--radius-md)', background: 'var(--amber-50)', border: '1px solid var(--amber-500)' }}>
                    <div style={{ ...eyebrow, color: 'var(--amber-600)', marginBottom: 4 }}>Ce qui manque pour répondre à l’auditeur</div>
                    <ul style={{ margin: 0, paddingInlineStart: 18, fontSize: 12.5, lineHeight: 1.6 }}>
                      {p.manques.map((m) => <li key={m}>{m}</li>)}
                    </ul>
                  </div>
                )}

                <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
                  La source se corrige au panneau du paramètre — cliquez son code ci-dessus. Un intervalle
                  publié ne se modifie pas : il se <strong>clôt</strong>, et un autre s’ouvre.
                </p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
